// App shell. One job: the reader. Quiet topbar = wordmark (home -> this week's
// parasha) + a parasha picker + a "?" help micro-panel + theme toggle, over a
// single content host. The chosen parasha lives in the URL hash, so a refresh
// keeps your place and a link is shareable (the app is literally "mareh makom" -
// a source reference). Theme is light by default, persisted. All controls are
// real, labelled buttons.

import { el, button } from "./lib/dom.ts";
import { renderReader, type ReaderSelection } from "./views/reader.ts";
import type { NavHandle } from "./views/navigator.ts";
import { openHelp } from "./views/help.ts";
import { PARSHIYOT, parashaByRef } from "./lib/parshiyot.ts";
import { currentWeekClock } from "./lib/calendar.ts";
import { getPrefs, listVerseVisited } from "./lib/store.ts";
import { matchesParasha, parashaVisitedCount, visitedUnderlineFraction } from "./lib/footprints.ts";
import { parseRoute, buildRoute, routeMatchesParasha } from "./lib/route.ts";
import * as navHistory from "./lib/navHistory.ts";
import { setSourceLangMode } from "./lib/sourceLang.ts";
import { hydrateReaderDisplay } from "./lib/readerDisplay.ts";
import { trapFocus } from "./lib/focusTrap.ts";
import { syncOverlayBackground, closeStrayOverlays } from "./lib/overlayBackground.ts";
import { applyTheme } from "./lib/theme.ts";

interface CurrentWeek {
  ref: string;
  en: string;
  he: string;
}

// Upper bound on how long a named-parasha deep link waits for hydrate() (saved theme /
// text size / translation) before the reader mounts. Only a wedged IndexedDB hits it.
const HYDRATE_WAIT_CAP_MS = 3000;

export function mountApp(root: HTMLElement): void {
  const shell = el("div", "shell");
  // "Your trail" rail open by default; collapsible (desktop). The collapse toggle lives
  // in the orientation bar (next to the breadcrumb) - see navigator renderOrientation.
  shell.dataset.railOpen = "true";

  const mark = el("button", "wordmark");
  mark.type = "button";
  // Text-only wordmark (the icon mark was removed 2026-06-22 - it read uneven
  // beside the text baseline). The wordmark text + the button's aria-label name
  // the app; the favicon / PWA icon still carry the scroll+trail artwork.
  mark.append(
    el("span", "wordmark-name", "TorahTrail"),
    el("span", "wordmark-sub", "Every verse has a trail."),
  );
  mark.setAttribute("aria-label", "TorahTrail - this week's parasha");

  const content = el("main", "content");
  content.id = "content";

  // ---- parasha picker -------------------------------------------------------
  const pickerLabel = el("span", "", "Parasha");
  const pickerBtn = el("button", "pickerbtn");
  pickerBtn.type = "button";
  pickerBtn.setAttribute("aria-haspopup", "dialog");
  pickerBtn.setAttribute("aria-expanded", "false");
  pickerBtn.append(pickerLabel, el("span", "caret"));

  let current: CurrentWeek | null = null;
  let routeSeq = 0; // guards against overlapping renders (route-race-1)
  let currentViewRef = ""; // the parasha ref currently shown (base for the deep-link hash)
  let currentNav: NavHandle | null = null; // the mounted navigator (warm history restores)

  const setLabel = (text: string): void => {
    pickerLabel.textContent = text;
  };

  // Handles doubled weeks (e.g. Matot-Masei) where Sefaria returns a combined ref
  // but the parshiyot table stores standalone halves (aria-current-doubled-1).
  const isCurrentParasha = (enName: string, ref: string): boolean => {
    if (!current) return false;
    if (ref === current.ref) return true;
    return current.en.split(/[-/]/).map((s) => s.trim()).includes(enName);
  };

  let overlay: { scrim: HTMLElement; panel: HTMLElement } | null = null;
  let pickerRelease: (() => void) | null = null;
  // Set while the picker is open: adds the "This week" marker to an already-built
  // picker when `current` lands late (start() calls it once /calendars resolves).
  let paintPickerWeek: (() => void) | null = null;

  function closePicker(): void {
    if (!overlay) return;
    pickerRelease?.();
    pickerRelease = null;
    paintPickerWeek = null;
    overlay.scrim.remove();
    overlay.panel.remove();
    overlay = null;
    syncOverlayBackground(); // un-inert the background BEFORE returning focus to it
    pickerBtn.setAttribute("aria-expanded", "false");
    pickerBtn.focus();
  }

  function openPicker(): void {
    if (overlay) {
      closePicker();
      return;
    }
    const scrim = el("div", "scrim");
    scrim.addEventListener("click", closePicker);

    const panel = el("div", "picker");
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");
    panel.setAttribute("aria-label", "Choose a parasha");

    // Typeahead over the 54 names (en + he substring, zero network). Filtering
    // hides chips + any book section that goes empty; clearing restores all.
    const filter = el("input", "picker-filter") as HTMLInputElement;
    filter.type = "search";
    filter.placeholder = "Find a parasha";
    filter.setAttribute("aria-label", "Find a parasha by name, English or Hebrew");
    panel.append(filter);

    const hashedRef = parseRoute(location.hash).ref;
    const entries: Array<{ chip: HTMLButtonElement; en: string; he: string; ref: string }> = [];
    const sections: Array<{ head: HTMLElement; list: HTMLElement }> = [];
    for (const b of PARSHIYOT) {
      const head = el("div", "picker-book");
      const heS = el("span", "", b.bookHe);
      heS.lang = "he";
      heS.dir = "rtl";
      head.append(heS, el("span", "en", b.book));
      const list = el("div", "picker-list");
      for (const p of b.parshiyot) {
        const chip = el("button", "pchip");
        chip.type = "button";
        chip.append(el("span", "pchip-en", p.en));
        // Hebrew name (+ optional "this week" marker) ride a right-aligned cluster so
        // every cell reads English-left / Hebrew-right in the uniform grid.
        const side = el("span", "pchip-side");
        const heName = el("span", "pchip-he", p.he);
        heName.lang = "he";
        heName.dir = "rtl";
        side.append(heName);
        chip.setAttribute("aria-label", `Open Parashas ${p.en}`);
        // "This week" is VISIBLE text, not aria-only (doubled weeks included).
        if (isCurrentParasha(p.en, p.ref)) side.append(el("span", "pchip-week", "This week"));
        chip.append(side);
        const isActive = hashedRef ? p.ref === hashedRef : isCurrentParasha(p.en, p.ref);
        if (isActive) chip.setAttribute("aria-current", "true");
        chip.addEventListener("click", () => pick(p.ref));
        list.append(chip);
        entries.push({ chip, en: p.en, he: p.he, ref: p.ref });
      }
      panel.append(head, list);
      sections.push({ head, list });
    }

    paintPickerWeek = (): void => {
      for (const e of entries) {
        if (e.chip.querySelector(".pchip-week") || !isCurrentParasha(e.en, e.ref)) continue;
        e.chip.querySelector(".pchip-side")?.append(el("span", "pchip-week", "This week"));
        if (!hashedRef) e.chip.setAttribute("aria-current", "true");
      }
    };

    filter.addEventListener("input", () => {
      for (const e of entries) e.chip.hidden = !matchesParasha(filter.value, e.en, e.he);
      for (const s of sections) {
        const empty = !s.list.querySelector(".pchip:not([hidden])");
        s.head.hidden = empty;
        s.list.hidden = empty;
      }
    });
    // Keyboard accelerator: Enter in the search opens the first still-visible match,
    // so "type a name + Enter" jumps straight there instead of tabbing the grid.
    filter.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      const first = panel.querySelector<HTMLButtonElement>(".pchip:not([hidden])");
      if (first) {
        e.preventDefault();
        first.click();
      }
    });
    // Arrow-key roving over the grid: Left/Right by one cell, Up/Down by a row,
    // Home/End to the ends - so a 54-item picker isn't ~55 Tab presses. In the
    // search field only ArrowDown dives in (the other keys stay text-editing).
    const ARROWS = ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End"];
    panel.addEventListener("keydown", (e) => {
      if (!ARROWS.includes(e.key)) return;
      if (document.activeElement === filter && e.key !== "ArrowDown") return;
      const chips = [...panel.querySelectorAll<HTMLButtonElement>(".pchip:not([hidden])")];
      if (!chips.length) return;
      const cur = chips.indexOf(document.activeElement as HTMLButtonElement);
      const firstTop = chips[0].offsetTop;
      const cols = Math.max(1, chips.filter((c) => c.offsetTop === firstTop).length);
      let next: number;
      if (cur < 0) next = 0; // from the search field -> first chip
      else if (e.key === "ArrowRight") next = Math.min(cur + 1, chips.length - 1);
      else if (e.key === "ArrowLeft") next = Math.max(cur - 1, 0);
      else if (e.key === "ArrowDown") next = Math.min(cur + cols, chips.length - 1);
      else if (e.key === "ArrowUp") next = Math.max(cur - cols, 0);
      else if (e.key === "Home") next = 0;
      else next = chips.length - 1;
      e.preventDefault();
      chips[next].focus();
    });

    // Footprint underlines: a quiet line that grows with the pesukim you have
    // opened sources on (count-scaled, soft-capped - NEVER a completion %).
    // Async + enhancement-only: chips render instantly, lines land when ready.
    void listVerseVisited()
      .then((visited) => {
        if (!visited.length) return;
        for (const e of entries) {
          const count = parashaVisitedCount(e.ref, visited);
          if (!count) continue;
          const trace = el("span", "pchip-trace");
          trace.setAttribute("aria-hidden", "true");
          trace.style.width = `${Math.round(visitedUnderlineFraction(count) * 100)}%`;
          e.chip.append(trace);
          e.chip.setAttribute(
            "aria-label",
            `Open Parashas ${e.en} - you explored sources on ${count} ${count === 1 ? "pasuk" : "pesukim"} here`,
          );
        }
      })
      .catch(() => {});

    shell.append(scrim, panel);
    overlay = { scrim, panel };
    syncOverlayBackground(); // inert the app behind the picker
    pickerBtn.setAttribute("aria-expanded", "true");

    // Move focus into the dialog and trap Tab within it via the shared, generic
    // focus trap (the same helper the journey/peek layers use - one implementation,
    // dynamic focusable set, layer-stacked). closePicker() releases + returns focus.
    // The typeahead takes initial focus so keyboard users can search immediately.
    const handle = trapFocus(panel, { initialFocus: filter, onEscape: closePicker });
    pickerRelease = () => handle.release();
  }

  function pick(ref: string): void {
    closePicker();
    const target = buildRoute(ref); // parasha only - picking resets any open path
    if (location.hash === target) {
      void route(); // same parasha re-picked -> no hashchange fires, render directly
    } else {
      location.hash = target; // hashchange -> route()
    }
  }

  pickerBtn.addEventListener("click", openPicker);
  mark.addEventListener("click", () => {
    closePicker();
    if (location.hash) location.hash = "";
    else void route();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closePicker();
  });

  // ---- routing --------------------------------------------------------------
  // Build the reader selection for a parasha, including doubled-week matching
  // for the current week (aria-current-doubled-1).
  function selectionFor(p: { ref: string; en: string; he: string }): ReaderSelection {
    return { ref: p.ref, en: p.en, he: p.he };
  }

  // Does this parsed route target the SAME parasha the mounted navigator is showing?
  // An empty ref means "this week" - it matches the navigator whose parashaRef the
  // hashless cold-load resolved to. The ONE place this judgment lives, so a future
  // doubled-week aliasing change lands here, not in three predicates.
  function sameParasha(ref: string): boolean {
    if (!currentNav) return false;
    return routeMatchesParasha(ref, currentViewRef, currentNav.parashaRef);
  }

  async function route(): Promise<void> {
    const my = ++routeSeq;
    const stillCurrent = (): boolean => my === routeSeq; // skip a superseded paint (route-race-1)
    // Invalidate the handle BEFORE any await: a failed render must never leave
    // a previous parasha's navigator registered for warm applies (a traversal
    // against a detached instance would rewrite history entries to the wrong
    // parasha's route).
    currentNav = null;
    // A cold remount reuses .content / .orient-slot; evict any drawer/peek/modal left
    // open in the prior view (it lives on document.body and would otherwise float over
    // the fresh reader AND keep the reused landmarks inert+aria-hidden).
    closeStrayOverlays();
    // onMounted (not the return value) registers the handle, so the error
    // view's manual Retry - which re-runs renderReader internally - registers
    // its fresh navigator too. The routeSeq guard drops superseded mounts.
    const hooks = {
      path: parseRoute(location.hash).path,
      onMounted: (nav: NavHandle): void => {
        if (!stillCurrent()) return;
        currentNav = nav;
        // The fallback branch (boot calendar failed) renders whatever week the
        // reader itself resolved - adopt it, or warm-matching is dead all session.
        if (!currentViewRef) currentViewRef = nav.parashaRef;
        // Upgrade a hashless entry (cold weekly load, wordmark click) to the
        // resolved parasha route, so Back onto it warm-collapses instead of
        // full-remounting the reader.
        if (!parseRoute(location.hash).ref) {
          navHistory.replaceRoute(buildRoute(currentViewRef));
        }
      },
    };
    const { ref } = parseRoute(location.hash);
    const picked = ref ? parashaByRef(ref) : undefined;
    if (picked) {
      currentViewRef = picked.ref;
      setLabel(picked.en);
      await renderReader(content, selectionFor(picked), stillCurrent, hooks);
    } else if (current) {
      currentViewRef = current.ref;
      setLabel(current.en);
      await renderReader(content, { ref: current.ref, en: current.en, he: current.he }, stillCurrent, hooks);
    } else {
      currentViewRef = "";
      setLabel("This week");
      await renderReader(content, undefined, stillCurrent, hooks); // fallback if the calendar lookup failed
    }
  }

  // ---- history traversal router (v7) ----------------------------------------
  // popstate AND hashchange funnel here (a real browser fires BOTH for one
  // fragment traversal). The echo is suppressed two ways: an echo landing
  // while its own apply is in flight is dropped by the applyingRoute check
  // (so it can never cancel the directional first pass), and an echo landing
  // after the drain finished re-applies as a silent no-op (applyRoute only
  // announces when the chain changed or a direction is known). Latest-wins
  // drain: a rapid second Back abandons the in-flight walk between awaits.
  interface PendingTraversal {
    route: string;
    direction?: "back" | "forward";
  }
  let pending: PendingTraversal | null = null;
  let applyingRoute: string | null = null;
  let applyRunning = false;

  async function onTraverse(direction?: "back" | "forward"): Promise<void> {
    if (location.hash === applyingRoute) return; // the echo of the in-flight apply
    navHistory.ensureStamped(location.hash);
    pending = { route: location.hash, direction };
    if (applyRunning) return; // the running drain picks it up
    applyRunning = true;
    try {
      while (pending !== null) {
        const p = pending;
        pending = null;
        applyingRoute = p.route;
        const { ref, path } = parseRoute(p.route);
        // Warm when the mounted navigator renders the route's parasha. An
        // empty ref means "this week" - it matches the navigator whose
        // parashaRef the hashless cold-load resolved to.
        if (sameParasha(ref) && currentNav) {
          await currentNav.applyRoute(path, { cancelled: () => pending !== null, direction: p.direction });
        } else {
          await route(); // parasha change / cold fallback: full remount + cold replay
        }
        applyingRoute = null;
      }
    } finally {
      applyingRoute = null;
      applyRunning = false;
    }
  }

  window.addEventListener("popstate", (e) => {
    void onTraverse(navHistory.notePop((e as PopStateEvent).state));
  });
  window.addEventListener("hashchange", () => void onTraverse());

  // ---- assemble -------------------------------------------------------------
  const helpBtn = button("helpbtn", "?", openHelp);
  helpBtn.setAttribute("aria-label", "How TorahTrail works");
  helpBtn.setAttribute("aria-haspopup", "dialog");
  helpBtn.title = "How TorahTrail works";
  // Mobile-only host the navigator fills with Bookmarks + Saved Paths (mirrors
  // .botnav-host so the counts stay live). Hidden on desktop, where the
  // orientation bar carries those actions. The theme toggle moved into the
  // reader's Tools menu - it does not need header prominence.
  const headeracts = el("div", "headeracts");
  // Tools (display settings) slot - the reader fills it each mount, like .orient-slot.
  // Sits between the parasha picker and the Bookmarks/Saved-Paths actions.
  const headerTools = el("div", "headertools");
  // hairline separating the prominent parasha menu from the utility icons
  const topSep = el("span", "topsep");
  topSep.setAttribute("aria-hidden", "true");
  const right = el("div", "topright");
  right.append(pickerBtn, topSep, headerTools, headeracts, helpBtn);

  const top = el("header", "topbar");
  top.append(mark, right);

  // Skip-to-content for keyboard users (a11y-8). Focuses the reader without
  // changing the hash (preventDefault), so it does not trigger a re-route.
  const skip = el("a", "skip", "Skip to text");
  skip.setAttribute("href", "#content");
  skip.addEventListener("click", (e) => {
    e.preventDefault();
    content.tabIndex = -1;
    content.focus();
  });

  // Full-width top slot for the orientation bar (the GPS "top"): spans all columns
  // on desktop, sticky under the header on mobile. The reader mounts the navigator's
  // orientation bar + aria-live region here each route.
  const orientSlot = el("div", "orient-slot");

  // LEFT rail = "where you came from": the always-visible Journey panel (the
  // navigator fills it by class). RIGHT rail = "where you can go next": the Explore
  // Next panel. Both are desktop-only; mobile uses the bottom nav + drawers.
  const railJourney = el("aside", "rail-journey");
  railJourney.setAttribute("aria-label", "Your trail - where you came from");
  const railExplore = el("aside", "rail-explore");
  railExplore.setAttribute("aria-label", "Explore next - where you can go");

  // Mobile-only bottom nav host (Trail / Explore / Start); the navigator fills it.
  const botnavHost = el("div", "botnav-host");

  const foot = el("footer", "foot");
  const pj = el("a", "foot-link", "powerjews.com");
  pj.setAttribute("href", "https://powerjews.com");
  pj.setAttribute("target", "_blank");
  pj.setAttribute("rel", "noopener noreferrer");
  foot.append("powered by ", pj, " · Besiyata Dishmaya");
  shell.append(skip, top, orientSlot, railJourney, content, railExplore, botnavHost, foot);
  root.replaceChildren(shell);

  const hydrated = hydrate();
  void start();

  // First-visit nudge toward the "?" help (heuristics: help discoverability for a
  // dummy-proof first run). One-shot + dismissible, stored under a dedicated
  // localStorage key (NOT Prefs, so the store contract + its tests are untouched).
  try {
    if (!localStorage.getItem("mm-help-seen")) {
      const hint = el("div", "firstrun-hint");
      hint.setAttribute("role", "note");
      hint.append(el("span", "firstrun-hint-txt", "New here? The ? button shows how TorahTrail works."));
      const dismissHint = (): void => {
        try {
          localStorage.setItem("mm-help-seen", "1");
        } catch {
          /* ignore - the hint still dismisses for this session */
        }
        hint.remove();
      };
      const got = button("firstrun-hint-x", "Got it", dismissHint);
      got.setAttribute("aria-label", "Dismiss the welcome hint");
      hint.append(got);
      helpBtn.addEventListener("click", dismissHint, { once: true });
      document.body.append(hint);
    }
  } catch {
    /* no localStorage -> skip the hint entirely */
  }

  // DEV-only source-integrity console hook (never bundled into prod - the
  // dynamic imports sit behind import.meta.env.DEV). In the console:
  //   window.__sourceIntegrity()                  -> audit the LIVE session
  //     graph (every edge/passage the Explore panel actually derived this
  //     session, recorded via sourceGraphSession.recordAdapted)
  //   window.__sourceIntegrity({ fixtures: true }) -> audit the synthetic
  //     fixture dataset (planted defects; for exercising the report itself)
  if (import.meta.env.DEV) {
    void Promise.all([
      import("./lib/sourceIntegrity.ts"),
      import("./lib/sourceGraphSession.ts"),
      import("./lib/sourceGraphFixtures.ts"),
    ]).then(([integrity, session, fixtures]) => {
      (window as unknown as Record<string, unknown>).__sourceIntegrity = (opts?: {
        fixtures?: boolean;
      }): unknown => {
        const store = opts?.fixtures ? fixtures.fixtureStore() : session.getSessionStore();
        const report = integrity.integrityReport(store);
        console.log(integrity.formatIntegrityReport(report));
        return report;
      };
    });
  }

  // Resolve the current week (for the default view + picker label/clock) and route
  // to whatever the hash asks for. route() reads `current` only when the hash does
  // NOT name a parasha, so a hash that does routes immediately and /calendars
  // resolves in parallel; an empty/unknown hash still waits for the week first.
  async function start(): Promise<void> {
    // Seed the CURRENT history entry (cold load creates no new entry; a cold
    // deep link IS entry 0, so in-app Back falls back to structural back).
    navHistory.init(location.hash);
    const weekReady = (async (): Promise<void> => {
      try {
        const c = await currentWeekClock();
        current = { ref: c.week.ref, en: c.week.parasha, he: c.week.parashaHe };
      } catch {
        current = null;
      }
      paintPickerWeek?.(); // a picker opened while this was in flight gets its marker now
    })();
    const { ref } = parseRoute(location.hash); // same test as route()'s `picked`
    if (ref && parashaByRef(ref)) {
      // The calendar round-trip used to give hydrate() time to seed the saved text
      // size / translation before the reader mounted; a SW-cached text can now beat
      // it. So route as soon as hydrate() lands (normally a few ms), or once the week
      // settles (the old behavior), or after a generous cap - the cap only exists so a
      // wedged IndexedDB can never hold the reader hostage. A short cap would mount the
      // reader at default size and let the first A+ overwrite the saved size.
      await Promise.race([hydrated, weekReady, new Promise<void>((r) => setTimeout(r, HYDRATE_WAIT_CAP_MS))]);
    } else {
      await weekReady;
    }
    await route();
  }
}

async function hydrate(): Promise<void> {
  try {
    const prefs = await getPrefs();
    applyTheme(prefs.theme);
    // Seed the "Show sources" display mode before (in practice) the reader
    // mounts - IndexedDB resolves well before the network-bound start(). Like
    // the theme, a lost race only means one render at the default; the control
    // and lists self-correct on the next interaction.
    setSourceLangMode(prefs.sourceLang);
    if (prefs.sourceLang === "default") delete document.documentElement.dataset.srclang;
    else document.documentElement.dataset.srclang = prefs.sourceLang;
    // Seed reader text-size + translation visibility before the reader mounts.
    hydrateReaderDisplay(prefs.textScale, prefs.showTranslation);
    // Seed the "Your trail" rail open/collapsed state (the orient-bar toggle reads it).
    const shellEl = document.querySelector(".shell");
    if (shellEl instanceof HTMLElement) shellEl.dataset.railOpen = prefs.railPinned ? "true" : "false";
  } catch {
    applyTheme("light");
  }
}
